import { CompRef, DocumentServicesObject } from '@wix/document-services-types';
import { ManifestServices } from '@wix/editor-platform-manifest-services';
import {
  createBuildPathsFn,
  findBuilderComponentTypes,
  isBuilderComponentType,
  loadAndProcessManifests,
  loadAndProcessManifestsByCompTypes,
  forceLoadAndProcessManifestsByCompTypes,
} from '../../utils/builderServices';
import { EventType } from '@wix/editor-platform-sdk-types';
import type { EditorPointerAPI } from '@wix/editorx-core-api';
import { PanelsStore } from './panelsStore';

type Event = {
  type: EventType;
  args: {
    eventPayload: {
      compType: string;
      compRef: CompRef;
      appDefinitionId: string;
    };
  };
};

function createManifestLoadListeners(
  documentServicesAPI: DocumentServicesObject,
  editorPointerAPI: EditorPointerAPI,
  manifestServices: ManifestServices,
  panelsStore: PanelsStore,
) {
  const buildPaths = createBuildPathsFn(
    documentServicesAPI,
    manifestServices.getManifestsByCompTypes,
  );

  function handleNavigateToPage(updates?: string[]): void {
    if (updates?.length && updates?.includes('pages.navigateTo')) {
      loadAndProcessManifests({
        documentServicesAPI,
        editorPointerAPI,
        manifestServices,
        panelsStore,
      });
    }
  }

  function handleCustomAppEvents(event: Event): void {
    if (typeof event === 'string') {
      try {
        event = JSON.parse(event);
      } catch (e) {
        // do nothing
      }
    }

    if (event.type === EventType.componentAddedToStage) {
      const compType = event.args.eventPayload.compType;
      const compRef = event.args.eventPayload.compRef;

      if (!isBuilderComponentType(compType)) {
        return;
      }

      loadAndProcessManifestsByCompTypes({
        documentServicesAPI,
        editorPointerAPI,
        manifestServices,
        panelsStore,
        compTypes: [compType],
        customBuildPaths: (loadedCompTypes) =>
          buildPaths(loadedCompTypes, { compRef, compType }),
      });
    } else if (event.type === EventType.appUpdateCompleted) {
      const appDefinitionId = event.args.eventPayload.appDefinitionId;
      const appData =
        documentServicesAPI.platform.getAppDataByAppDefId(appDefinitionId);
      const builderComponentTypes = findBuilderComponentTypes(appData);
      forceLoadAndProcessManifestsByCompTypes({
        documentServicesAPI,
        editorPointerAPI,
        manifestServices,
        panelsStore,
        compTypes: builderComponentTypes,
      });
    }
  }

  return {
    handleNavigateToPage,
    handleCustomAppEvents,
  };
}

export { createManifestLoadListeners };
